import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Quotation,
  LineItem,
  QuotationSection,
  PaymentMilestone,
  RevisionLogEntry,
  Brand,
  CatalogItem,
  BrandPricing,
  GlobalSettings,
} from '@/types/quotation';
import type { CatalogueStoreItem as PrebuiltItem } from './slices/catalogSlice';
import type { QuotationRevision } from '@/types/revision';

// Slices
import { FinancialSlice, createFinancialSlice } from './slices/financialSlice';
import { QuotationManagementSlice, createQuotationManagementSlice } from './slices/quotationManagementSlice';
import { RoomManagementSlice, createRoomManagementSlice } from './slices/roomManagementSlice';
import { CatalogSlice, createCatalogSlice } from './slices/catalogSlice';
import { HistorySlice, createHistorySlice } from './slices/historySlice';

import { QuotationStore } from './quotationStore.types';

export const useQuotationStore = create<QuotationStore>()(
  persist(
    (set, get, api) => ({
      // Shared Initial State
      currentQuotation: null,
      quotations: [],
      revisions: [],
      isLoading: false,
      error: null,
      autoSaveEnabled: false,
      
      // UI Wizard State
      activeStep: 1,
      setActiveStep: (step) => set({ activeStep: step }),

      // Draft & Sync State
      syncStatus: 'idle',
      localDrafts: {},
      setSyncStatus: (status) => set({ syncStatus: status }),

      // Combine Slices
      ...createFinancialSlice(set, get, api),
      ...createQuotationManagementSlice(set, get, api),
      ...createRoomManagementSlice(set, get, api),
      ...createCatalogSlice(set, get, api),
      ...createHistorySlice(set, get, api),
    }),
    {
      name: 'dpl-quotation-storage',
      // Only persist settings and local client drafts — do NOT persist currentQuotation 
      // to avoid stale quote IDs being loaded after a quote is deleted from the DB.
      partialize: (state) => ({
        localDrafts: state.localDrafts,
      }),
    }
  )
);
